import React, { useState, useEffect } from 'react';
import { Modal, Form, Checkbox, Select, Input, message, Space, Tag } from 'antd';
import { MailOutlined, WhatsAppOutlined, MessageOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { TextArea } = Input;

/**
 * Manual Reminder Modal Component
 *
 * Props:
 * - visible: boolean - controls modal visibility
 * - onClose: function - callback when modal closes
 * - entityType: string - 'invoice', 'quote', or 'lead'
 * - entityId: string - UUID of the entity
 * - entityDisplay: string - display name for the entity (e.g., "INV-2024-001")
 */
const ManualReminderModal = ({ visible, onClose, entityType, entityId, entityDisplay }) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  useEffect(() => {
    if (visible) {
      fetchTemplates();
    }
  }, [visible]);

  const fetchTemplates = async () => {
    try {
      // Fetch email templates
      const emailResponse = await axios.get('/template/template/', {
        params: { template_type: 'EMAIL' }
      });
      if (emailResponse.data) {
        setEmailTemplates(emailResponse.data.results || emailResponse.data || []);
      }

      // Fetch WhatsApp templates
      const whatsappResponse = await axios.get('/reminders/whatsapp-templates/approved/');
      if (whatsappResponse.data) {
        setWhatsappTemplates(whatsappResponse.data.results || whatsappResponse.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Validate at least one channel is selected
      if (!values.send_email && !values.send_whatsapp && !values.send_sms) {
        messageApi.error('Please select at least one channel (Email, WhatsApp, or SMS)');
        return;
      }

      setLoading(true);

      const payload = {
        entity_type: entityType,
        entity_id: entityId,
        send_email: values.send_email || false,
        send_whatsapp: values.send_whatsapp || false,
        send_sms: values.send_sms || false,
        email_template_id: values.email_template_id,
        whatsapp_template_id: values.whatsapp_template_id,
        custom_subject: values.custom_subject,
        custom_body: values.custom_body,
        additional_emails: values.additional_emails
          ? values.additional_emails.split(',').map(e => e.trim()).filter(e => e)
          : [],
        notes: values.notes,
      };

      const response = await axios.post('/reminders/manual/send/', payload);

      if (response.status === 201) {
        messageApi.success('Manual reminder sent successfully');
        form.resetFields();
        onClose();
      }
    } catch (error) {
      messageApi.error('Failed to send manual reminder');
      console.error('Error sending reminder:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={`Send Manual Reminder - ${entityDisplay}`}
        open={visible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={700}
        confirmLoading={loading}
        okText="Send Reminder"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Select Channels" required>
            <Space direction="vertical">
              <Form.Item name="send_email" valuePropName="checked" noStyle>
                <Checkbox>
                  <MailOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                  Send via Email
                </Checkbox>
              </Form.Item>
              <Form.Item name="send_whatsapp" valuePropName="checked" noStyle>
                <Checkbox>
                  <WhatsAppOutlined style={{ color: '#25D366', marginRight: 8 }} />
                  Send via WhatsApp
                </Checkbox>
              </Form.Item>
              <Form.Item name="send_sms" valuePropName="checked" noStyle>
                <Checkbox>
                  <MessageOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                  Send via SMS
                </Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.send_email !== currentValues.send_email}
          >
            {({ getFieldValue }) =>
              getFieldValue('send_email') ? (
                <>
                  <Form.Item
                    name="email_template_id"
                    label="Email Template (Optional)"
                  >
                    <Select
                      placeholder="Select email template"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      {emailTemplates.map(template => (
                        <Option key={template.id} value={template.id}>
                          {template.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item name="custom_subject" label="Custom Subject (Optional)">
                    <Input placeholder="Leave empty to use template subject" />
                  </Form.Item>

                  <Form.Item name="custom_body" label="Custom Message (Optional)">
                    <TextArea
                      rows={4}
                      placeholder="Leave empty to use template body"
                    />
                  </Form.Item>

                  <Form.Item name="additional_emails" label="Additional Email Recipients (Optional)">
                    <Input placeholder="Separate multiple emails with commas" />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.send_whatsapp !== currentValues.send_whatsapp}
          >
            {({ getFieldValue }) =>
              getFieldValue('send_whatsapp') ? (
                <Form.Item
                  name="whatsapp_template_id"
                  label="WhatsApp Template"
                  rules={[{ required: getFieldValue('send_whatsapp'), message: 'Please select a WhatsApp template' }]}
                >
                  <Select
                    placeholder="Select WhatsApp template"
                    showSearch
                    optionFilterProp="children"
                  >
                    {whatsappTemplates.map(template => (
                      <Option key={template.id} value={template.id}>
                        <Space>
                          {template.name}
                          <Tag color="blue">{template.template_language}</Tag>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item name="notes" label="Internal Notes (Optional)">
            <TextArea
              rows={2}
              placeholder="Add any notes about this manual reminder (not sent to customer)"
            />
          </Form.Item>

          <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', marginTop: '16px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              <strong>Note:</strong> The reminder will be sent to the contact associated with this {entityType}.
              {entityType === 'invoice' && ' For invoices, it will be sent to the account\'s primary contact.'}
              {entityType === 'quote' && ' For quotes, it will be sent to the account\'s primary contact.'}
              {entityType === 'lead' && ' For leads, it will be sent to the lead\'s email/phone number.'}
            </p>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ManualReminderModal;
